import { observable, action, computed, toJS } from 'mobx';
import * as api from '../services/api';
import UserStore from './common/UserStore';
import { saveToLocalStorage, getFromLocalStorage } from '../services/utils';
import moment from 'moment';
import ScanningStore from './common/ScanningStore';
import { getCashierScreenResponse } from '../services/api';

class LiveDispatchStore {
    @observable date = moment().format('DD-MM-YYYY');
    @observable loading = false;
    @observable routes = [];
    @observable panduMasukOrders = [];
    @observable panduLaluOrders = [];
    @observable dineInOrders = [];
    @observable pickUpOrders = [];
    @observable inStoreOrders = [];
    @observable inCartOrders = [];
    @observable bags = [];
    @observable instaServiceProductions = [];
    @observable currentWarmerStocks = [];

    //variables for scanning barcode
    @observable scannedItem = null;
    @observable scannedItemSKU = '';
    @observable scannedItemError = '';
    @observable showScannedItemOnLiveDispatch = false;
    @observable itemScanLoading = false;

    //variables for image upload functionality
    @observable isImageUploading = false;
    @observable showImage = false;
    @observable itemImage = null;
    @observable originalItemImageLink = null;

    @observable remakeLoading = false;
    @observable SKURemakeErrorMsg = '';
    @observable closedAggregatorStores = [];
    @observable recentlyClosedAggregatorStores = [];
    @observable dispatchesOnDate = [];
    @observable salePlatforms = [];
    @observable updatedSalePlatforms = [];
    @observable outletSetting = null;
    @observable pendingOutletSetting = null;
    @observable outletControlLoading = false;
    @observable truckAlerts = [];
    @observable recentTruckAlerts = [];
    @observable driveThroughAlerts = [];
    @observable carArrivedAlerts = [];
    @observable needHelpAlerts = [];
    @observable thirdPartyDeliveryErrorMessage = '';
    @observable orderNumber = [];
    @observable orderMarkedDoneErrorMessage = null;
    @observable cardTerminalNotConnected = false;
    @observable showCardTerminalNotConnectedModal = false;
    @observable newPanduMasukOrder = getFromLocalStorage('newPanduMasukOrder') || [];
    @observable voiceReminders = [];
    @observable outletPaused = false;

    closingDispatchReason = {
        RAIN: 'RAIN',
        PRODUCTION_FAILURE: 'PRODUCTION_FAILURE',
        RIDER_SHORTAGE: 'RIDER_SHORTAGE',
        OUT_OF_HOURS: 'OUT_OF_HOURS',
        TECH_ISSUES: 'TECH_ISSUES',
        HUB_ISSUES: 'HUB_ISSUES',
        OTHER: 'Other',
    };

    closeDurationList = [
        { id: 30, name: '30 mins' },
        { id: 60, name: '1 hr' },
        { id: 1440, name: '24 hrs' },
    ];

    routesLastModified = null;
    instaServiceLastModified = null;
    newOrderAudio = new Audio(`${process.env.PUBLIC_URL}/adaNewOrder.mp3`);

    // bag status
    CANCELLED = 'Cancelled';

    // item status
    ON_ROUTE = 'ON_ROUTE';
    IN_WARMER = 'IN_WARMER';
    IN_PRODUCTION = 'IN_PRODUCTION';

    @action resetProductionLastModified = () => {
        this.routesLastModified = null;
    };

    normalizeBagNumber = (bagNumber) => {
        if (bagNumber == null) {
            return null;
        }

        const normalizedBagNumber = `${bagNumber}`.trim();
        return normalizedBagNumber ? normalizedBagNumber : null;
    };

    getPanduMasukBagNumber = (order) => {
        if (!order) {
            return null;
        }

        const bag = order.bags && order.bags[0] ? order.bags[0] : order;
        if (!bag || bag.order_mode !== "PANDU_MASUK") {
            return null;
        }

        return this.normalizeBagNumber(bag.bag_number);
    };

    saveNewPanduMasukOrders = () => {
        saveToLocalStorage('newPanduMasukOrder', this.newPanduMasukOrder);
    };

    @action rememberPanduMasukCustomerWaiting = (bagNumber) => {
        const normalizedBagNumber = this.normalizeBagNumber(bagNumber);
        if (!normalizedBagNumber) {
            return;
        }

        const alreadyTracked = this.newPanduMasukOrder.some(
            (existingBagNumber) => this.normalizeBagNumber(existingBagNumber) === normalizedBagNumber
        );

        if (!alreadyTracked) {
            this.newPanduMasukOrder.push(normalizedBagNumber);
            this.saveNewPanduMasukOrders();
        }
    };

    @action syncPanduMasukCustomerWaitingWithActiveOrders = () => {
        const activeBagNumbers = new Set(
            []
                .concat(this.bags || [])
                .concat(this.dineInOrders || [])
                .concat(this.panduMasukOrders || [])
                .map(this.getPanduMasukBagNumber)
                .filter(Boolean)
        );

        this.newPanduMasukOrder = (this.newPanduMasukOrder || [])
            .map(this.normalizeBagNumber)
            .filter((bagNumber) => bagNumber && activeBagNumbers.has(bagNumber));
        this.saveNewPanduMasukOrders();
    };

    @action setItemImage = (image) => {
        if (image) {
            this.itemImage = image;
        }
    };

    errorCallbackRemakeAPI = (errorObject) => {
        if (errorObject.response) {
            errorObject.response.json().then((body) => {
                if (body.message) {
                    this.SKURemakeErrorMsg = body.message;
                }
            });
        }
    };
    errorCallbackSKUScanAPI = (errorObject) => {
        if (errorObject.response) {
            errorObject.response.json().then((body) => {
                if (body.message) {
                    this.scannedItemError = body.message;
                }
            });
        }
    };

    @action handleUploadImage = (image, item) => {
        this.isImageUploading = true;
        api.uploadItemImage(image, item)
            .then((res) => {
                this.showImage = true;
                setTimeout(() => {
                    this.showImage = false;
                }, 5000);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                this.isImageUploading = false;
            });
    };

    @action handleRemakeSKUItem = (item, callback) => {
        this.remakeLoading = true;
        let status;
        api.handleRemakeSKUItem(item, this.errorCallbackRemakeAPI)
            .then((res) => {
                status = 'SUCCESS';
            })
            .catch((err) => {
                console.log(err);
                status = 'FAILED';
            })
            .finally(() => {
                this.remakeLoading = false;
                callback && callback(status);
            });
    };

    @action handleReprintSKUItem = (item, callback) => {
        let status;
        api.handleReprintSKUItem(item, this.errorCallbackRemakeAPI)
            .then((res) => {
                status = 'SUCCESS';
            })
            .catch((err) => {
                console.log(err);
                status = 'FAILED';
            })
            .finally(() => {
                callback && callback(status);
            });
    };

    @action handledScannedItem = (
        todayMenuId,
        incrementalNumber,
        pickUpNumber = null
    ) => {
        this.showScannedItemOnLiveDispatch = true;
        this.itemScanLoading = true;

        api.handledScannedItem(
            todayMenuId,
            this.date,
            incrementalNumber,
            pickUpNumber,
            this.errorCallbackSKUScanAPI
        )
            .then((res) => {
                this.scannedItem = res;
                let groupedItem = null;
                if (this.scannedItem.order_mode == "DELIVERY") {
                    groupedItem = this.getScannedItemGroup(this.bags, this.scannedItem.item.order_number, this.scannedItem.item.item_group);
                } else {
                    groupedItem = this.getScannedItemGroup(this.dineInOrders, this.scannedItem.item.order_number, this.scannedItem.item.item_group);
                }

                this.scannedItem.groupedItems = groupedItem; 
                this.scannedItemError = '';
                this.originalItemImageLink = res.dispatch_scan_example_image;
                if (this.itemImage) {
                    const imageToBeUploaded = String(
                        this.itemImage.split('base64,')[1]
                    ); //removing the pre-string from the image
                    this.handleUploadImage(imageToBeUploaded, res.item);
                }
                if (res.sound_file) {
                    const audio = new Audio(
                        `${process.env.PUBLIC_URL}/${res.sound_file}`
                    );
                    audio.play()
                        .catch(error => {
                            console.log("Error playing sound alert");
                        });
                }
            })
            .catch((err) => {
                this.scannedItem = null;
                console.log(err);
            })
            .finally(() => {
                this.itemScanLoading = false;

                setTimeout(() => {
                    this.showScannedItemOnLiveDispatch = false;
                }, 5 * 1000);
            });
    };

    @action getScannedItemGroup = (orders, orderNumber, itemGroup) => {
        if (!itemGroup) {
            return null;
        }

        const order = orders.find(order => order.order_number === orderNumber);
        if (!order || !order.items) {
            return null;
        }

        const [major] = itemGroup.split('.');

        const groupedItems = order.items
        .filter(item => {
            if (!item.item_group) return false;
            const [itemMajor] = item.item_group.split('.');
            return itemMajor === major;
        })
        .sort((a, b) => {
            const aMinor = Number(a.item_group.split('.')[1]);
            const bMinor = Number(b.item_group.split('.')[1]);
            return aMinor - bMinor;
        });

        const mainItem = groupedItems.find(item => item.item_group.endsWith('.0')) || null;
        const comboItems = groupedItems.filter(item => !item.item_group.endsWith('.0'));

        return {mainItem, comboItems, allItems: groupedItems};

    }

    @action groupItems(items) {
        const grouped = {};
        const standalone = [];

        items.forEach(item => {
            if (!item.item_group) {
                standalone.push(item);
                return;
            }

            const [major, minor] = item.item_group.split('.').map(Number);

            if (!grouped[major]) {
                grouped[major] = { main: null, combos: [] };
            }

            if (minor === 0) {
                grouped[major].main = item;
            } else {
                grouped[major].combos.push(item);
            }
        });

        return { grouped, standalone };
    }

    @action handleScanPopup = () => {
        this.showScannedItemOnLiveDispatch = false;
    }

    @action handledScannedWarmerItem = (
        productionId,
        sku,
        quantity
    ) => {
        this.showScannedItemOnLiveDispatch = true;
        this.itemScanLoading = true;

        api.handledScannedWarmerItem(
            productionId,
            sku,
            quantity,
            this.errorCallbackSKUScanAPI
        )
            .then((res) => {
                if (!res.items) {
                    this.scannedItemError = 'Sticker already scanned';
                    this.scannedItem = null;
                } else {
                    this.scannedItem = res;
                    this.scannedItem.isWarmerItem = true;
                    this.scannedItemSKU = sku;
                    this.scannedItemError = '';
                    this.originalItemImageLink = res.dispatch_scan_example_image;
                    if (this.itemImage) {
                        this.showImage = true;
                        setTimeout(() => {
                            this.showImage = false;
                        }, 5000);
                    }
                }
            })
            .catch((err) => {
                this.scannedItem = null;
                console.log(err);
            })
            .finally(() => {
                this.itemScanLoading = false;

                setTimeout(() => {
                    this.showScannedItemOnLiveDispatch = false;
                }, 5 * 1000);
            });
    };

    @action handledScannedBag = (pickUpNumber) => {
        this.showScannedItemOnLiveDispatch = true;
        this.itemScanLoading = true;
        const bagImage = this.itemImage ? String(this.itemImage.split('base64,')[1]) : null;

        api.handledScannedBagSticker(pickUpNumber, bagImage, this.errorCallbackSKUScanAPI)
            .then((res) => {
                this.scannedItem = res;
                this.scannedItem.bag = true;
            })
            .catch((err) => {
                console.log(err);
                this.itemImage = null;
            })
            .finally(() => {
                this.itemScanLoading = false;

                setTimeout(() => {
                    this.showScannedItemOnLiveDispatch = false;
                }, 5 * 1000);
            });
    };

    @action sendRouteDispatch = (routeid) => {
        this.thirdPartyDeliveryErrorMessage = false;
        this.loading = true;
        //Third party delivery route id container Route - orderId
        if (routeid.includes("Route -")) {
            api.sendRetryDelivery(routeid.slice(routeid.indexOf("Route - ") + 8))
            .then((res) => {
                if (res) {
                    const { responseBody } = res;
                    responseBody.results.map((o) => {
                        if (o.error_message) {
                            this.thirdPartyDeliveryErrorMessage = true;
                            this.orderNumber.push(o.order_numbers);
                        }
                    });
                }
                this.loading = false;
            })
            .catch((err) => {
                UserStore.message = err.message;
            });
            return;
        }

        api.sendRoute(routeid)
            .then((res) => {
                if (res) {
                    const { lastModified, responseBody } = res;
                    responseBody.results.map((o) => {
                        if (o.error_message) {
                            this.thirdPartyDeliveryErrorMessage = true;
                            this.orderNumber.push(o.order_numbers);
                        }
                    });
                }
                this.loading = false;
            })
            .catch((err) => {
                UserStore.message = err.message;
            });
    };

    @action handleGetDispatches = (forceLatest = false, date = this.date) => {
        this.date = date;
        let ifModifiedSince = null;
        if (!forceLatest) {
            ifModifiedSince = this.routesLastModified;
        }

        api.getRoutes(this.date, ifModifiedSince)
            .then((res) => {
                if (res.modified) {
                    console.log('res.modified');
                    const { lastModified, responseBody } = res;
                    const { bags, routes } = responseBody;
                    this.routesLastModified = lastModified;

                    const sortedBags =
                        bags &&
                        bags.sort((a, b) => a.bag_number - b.bag_number);
                    this.bags = sortedBags ? [...sortedBags] : [];

                    const pickUpOrders = this.bags
                        .filter((bag) => bag.customer_self_pickup_at_outlet && bag.order_mode != "PANDU_MASUK" && bag.order_mode != "PANDU_LALU")
                        .map((bag) => {
                            const {
                                amount_due,
                                delivery_id,
                                delivery_time,
                                order_number,
                                payment_method,
                                pre_tax_net_total,
                            } = bag;
                            return {
                                isPickUpOrder: true,
                                amount_due,
                                order_number,
                                delivery_id,
                                start_date: delivery_time,
                                payment_method,
                                pre_tax_net_total,
                                bags: [bag],
                            };
                        });
                    
                    const panduMasukOrders = this.bags
                        .filter((bag) => bag.customer_self_pickup_at_outlet && bag.order_mode == "PANDU_MASUK")
                        .map((bag) => {
                            const {
                                amount_due,
                                delivery_id,
                                delivery_time,
                                order_number,
                                payment_method,
                                pre_tax_net_total,
                            } = bag;
                            return {
                                isPickUpOrder: true,
                                amount_due,
                                order_number,
                                delivery_id,
                                start_date: delivery_time,
                                payment_method,
                                pre_tax_net_total,
                                bags: [bag],
                            };
                        });

                    const panduLaluOrders = this.bags
                        .filter((bag) => bag.customer_self_pickup_at_outlet && bag.order_mode == "PANDU_LALU")
                        .map((bag) => {
                            const {
                                amount_due,
                                delivery_id,
                                delivery_time,
                                order_number,
                                payment_method,
                                pre_tax_net_total,
                            } = bag;
                            return {
                                isPickUpOrder: true,
                                amount_due,
                                order_number,
                                delivery_id,
                                start_date: delivery_time,
                                payment_method,
                                pre_tax_net_total,
                                bags: [bag],
                            };
                        });

                    this.panduMasukOrders = panduMasukOrders
                        .sort(
                            (a, b) =>
                                a.start_date - b.start_date
                        );

                    this.panduLaluOrders = panduLaluOrders
                        .sort(
                            (a, b) =>
                                a.start_date - b.start_date
                        );
                    this.syncPanduMasukCustomerWaitingWithActiveOrders();

                    const allOrders = routes.concat(pickUpOrders);

                    const sortedRoutes = allOrders
                        .filter(
                            (route) =>
                                route.bags.length &&
                                !route.bags.every(
                                    (bag) => bag.status === this.CANCELLED
                                )
                        )
                        .sort(
                            (a, b) =>
                                a.start_date - b.start_date ||
                                a.dispatch_no - b.dispatch_no ||
                                a.route_no - b.route_no
                        );
                    this.routes = sortedRoutes ? [...sortedRoutes] : [];
                }
                this.loading = false;
            })
            .catch((err) => {
                UserStore.message = err.message;
            });
    };

    @action handleGetDineInOrders = (forceLatest = false, date = this.date) => {
        this.date = date;
        let ifModifiedSince = null;
        if (!forceLatest) {
            ifModifiedSince = this.routesLastModified;
        }

        api.getDineInOrders(this.date, ifModifiedSince)
            .then((res) => {
                if (res.modified) {
                    const { lastModified, responseBody } = res;
                    const { list: dineInOrders, message } = responseBody;
                    this.routesLastModified = lastModified;
                    const newDineInOrders = dineInOrders
                        ? dineInOrders.filter(
                              (order) =>
                                (order.status !== "Completed" || order.order_mode === "PANDU_MASUK" || order.order_mode === "PANDU_LALU" ) &&                            
                                order.items &&
                                order.items.length > 0
                          )
                        : [];

                    const sortedDineInOrders =
                        newDineInOrders &&
                        newDineInOrders.sort(
                            (a, b) => a.order_date - b.order_date
                        );

                    this.dineInOrders = sortedDineInOrders
                        ? [...sortedDineInOrders]
                        : [];
                    this.syncPanduMasukCustomerWaitingWithActiveOrders();
                }
                this.loading = false;
            })
            .catch((err) => {
                UserStore.message = err.message;
            });
    };

    @action handleGetAggregatorStoreStatus = () => {
        this.recentlyClosedAggregatorStores = [];
        api.getAggregatorStoreStatus()
            .then((response) => {
                response.forEach((entry) => {
                    if (
                        entry.open === false &&
                        this.closedAggregatorStores.indexOf(
                            entry.sale_platform_name
                        ) === -1
                    ) {
                        this.closedAggregatorStores.push(
                            entry.sale_platform_name
                        );
                        this.recentlyClosedAggregatorStores.push(
                            entry.sale_platform_name
                        );
                    }
                    if (
                        entry.open === true &&
                        this.closedAggregatorStores.indexOf(
                            entry.sale_platform_name
                        ) > -1
                    ) {
                        this.closedAggregatorStores.splice(
                            this.closedAggregatorStores.indexOf(
                                entry.sale_platform_name
                            ),
                            1
                        );
                    }
                });
            })
            .catch((err) => console.log(err));
    };

    @action handleGetTruckAlerts = () => {
        api.getTruckStatus()
            .then((response) => {
                response.forEach((alert) => {
                    const TRUCK_ARRIVED = 'Truck has arrived';
                    const TRUCK_ARRIVING_IN_10_MINUTES = 'Truck arriving in 10 minutes';
                    if (alert.status === 'ARRIVED') {
                        alert.status = TRUCK_ARRIVED
                    } else if (alert.status === 'ARRIVING_IN_10_MINUTES') {
                        alert.status = TRUCK_ARRIVING_IN_10_MINUTES
                    }
                    this.recentTruckAlerts = [];
                    // Add to `recentTruckAlerts` only if not acknowledged
                    if (!alert.acknowledged) {
                        this.recentTruckAlerts.push(alert);
                    }
                })
                this.truckAlerts = response;
            })
        .catch((err) => console.log(err));
    };

    @action handleGetCardTerminalNotConnectedAlert = () => {
        api.getCardTerminalNotConnectedStatus()
            .then((response) => {
                this.cardTerminalNotConnected = response;
            })
        .catch((err) => console.log(err));
    };

    @action handleOpenStore = (store) => {
        api.openStore(store)
            .then((response) => {
                this.closedAggregatorStores.splice(
                    this.closedAggregatorStores.indexOf(store),
                    1
                );
                this.recentlyClosedAggregatorStores.splice(
                    this.recentlyClosedAggregatorStores.indexOf(store),
                    1
                );
            })
            .catch((err) => {
                UserStore.message =
                    'Store not opened successfully. Please try again';
                this.recentlyClosedAggregatorStores = [];
            });
    };

    formatDispatchStatus = (dispatch) => {
        if (!dispatch) {
            return {
                ...dispatch,
                status: 'Unknown',
                dispatched: false,
            };
        }

        const dispatched = moment().isAfter(
            moment(`${this.date} ${dispatch.order_cut_off}`, 'DD-MM-YYYY HH:mm a')
        );

        if (dispatch.closed === true) {
            return {
                ...dispatch,
                status: 'Closed',
                dispatched,
            };
        }

        if (dispatch.closed === false && dispatched) {
            return {
                ...dispatch,
                status: 'Dispatched',
                dispatched,
            };
        }

        return {
            ...dispatch,
            status: 'Open',
            dispatched,
        };
    };

    normalizeSalePlatform = (salePlatform) => {
        if (!salePlatform) {
            return null;
        }

        const normalizedPlatform = { ...salePlatform };
        if (normalizedPlatform.open == null) {
            normalizedPlatform.open = true;
        }

        if (normalizedPlatform.close_duration && normalizedPlatform.open_at) {
            const openAt = Number(normalizedPlatform.open_at);
            if (!Number.isNaN(openAt)) {
                normalizedPlatform.auto_open_at =
                    new Date(openAt).getDate() === new Date().getDate()
                        ? moment(openAt).local().format('hh:mm A')
                        : '11:59 PM';
            }
        } else {
            normalizedPlatform.auto_open_at = '';
        }

        if (normalizedPlatform.brand === 'popmeals') {
            normalizedPlatform.brand_name = 'Pop Meals';
        } else if (normalizedPlatform.brand === 'dahmakan') {
            normalizedPlatform.brand_name = 'Dahmakan';
        } else {
            normalizedPlatform.brand_name = normalizedPlatform.platform_name
                ? normalizedPlatform.platform_name.split(' - ')[0]
                : normalizedPlatform.aggregator;
        }

        return normalizedPlatform;
    };

    getSalePlatformKey = (salePlatform) => {
        if (!salePlatform) {
            return '';
        }

        return [
            salePlatform.aggregator,
            salePlatform.platform_name,
            salePlatform.brand,
            salePlatform.merchant_id,
            salePlatform.chain_code,
            salePlatform.remote_id,
            salePlatform.store_id,
        ]
            .filter(Boolean)
            .join('::');
    };

    replaceUpdatedSalePlatform = (salePlatform) => {
        const salePlatformKey = this.getSalePlatformKey(salePlatform);
        const nextUpdatedSalePlatforms = this.updatedSalePlatforms.filter(
            (entry) => this.getSalePlatformKey(entry) !== salePlatformKey
        );

        nextUpdatedSalePlatforms.push({ ...salePlatform });
        this.updatedSalePlatforms = nextUpdatedSalePlatforms;
    };

    @action handleGetDispatchStatus = (date = this.date) => {
        this.outletControlLoading = true;
        this.date = date;

        return api.getDispatchStatus(date)
            .then((response) => {
                const dispatchesOnDate = Array.isArray(response.dispatches_on_date)
                    ? response.dispatches_on_date
                    : [];

                this.dispatchesOnDate = dispatchesOnDate
                    .sort((a, b) => a.dispatch_no - b.dispatch_no)
                    .map((dispatch) => this.formatDispatchStatus(dispatch));
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    @action handleGetHubStatus = () => {
        this.outletControlLoading = true;

        return api.getHubStatusByCode()
            .then((hub) => {
                const salePlatforms = [];

                const pushIfValid = (platforms, validator) => {
                    if (!Array.isArray(platforms)) {
                        return;
                    }

                    platforms.forEach((salePlatform) => {
                        if (validator(salePlatform)) {
                            const normalizedSalePlatform = this.normalizeSalePlatform(salePlatform);
                            if (normalizedSalePlatform) {
                                salePlatforms.push(normalizedSalePlatform);
                            }
                        }
                    });
                };

                pushIfValid(
                    hub.grab_food_sale_platforms,
                    (salePlatform) => salePlatform && salePlatform.merchant_id != null
                );
                pushIfValid(
                    hub.food_panda_sale_platforms,
                    (salePlatform) =>
                        salePlatform &&
                        salePlatform.chain_code != null &&
                        salePlatform.remote_id != null
                );
                pushIfValid(
                    hub.shopee_food_sale_platforms,
                    (salePlatform) => salePlatform && salePlatform.store_id != null
                );

                this.salePlatforms = salePlatforms;
                this.updatedSalePlatforms = [];
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    formatOutletTime = (timeValue) => {
        if (!Array.isArray(timeValue) || timeValue.length < 2) {
            return '';
        }

        return moment({
            hour: timeValue[0],
            minute: timeValue[1],
        }).format('HH:mm');
    };

    buildOutletTimeArray = (timeValue) => {
        if (!timeValue) {
            return null;
        }

        const parsedTime = moment(timeValue, 'HH:mm');
        if (!parsedTime.isValid()) {
            return null;
        }

        return [parsedTime.hour(), parsedTime.minute()];
    };

    @action handleGetOutletSetting = () => {
        this.outletControlLoading = true;

        return api.getOutletSettingByHubCode()
            .then((outletSetting) => {
                this.outletSetting = outletSetting;
                this.pendingOutletSetting = {
                    ...(outletSetting || {}),
                    opening_time: this.formatOutletTime(outletSetting && outletSetting.opening_time),
                    closing_time: this.formatOutletTime(outletSetting && outletSetting.closing_time),
                };
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    @action setPendingOutletTime = (field, value) => {
        this.pendingOutletSetting = {
            ...(this.pendingOutletSetting || {}),
            [field]: value,
        };
    };

    @action handleUpdateOutletSetting = () => {
        if (!this.pendingOutletSetting) {
            return Promise.reject(new Error('Outlet setting is not loaded yet.'));
        }

        this.outletControlLoading = true;

        const openingTime = this.buildOutletTimeArray(this.pendingOutletSetting.opening_time);
        const closingTime = this.buildOutletTimeArray(this.pendingOutletSetting.closing_time);

        if (!openingTime || !closingTime) {
            this.outletControlLoading = false;
            return Promise.reject(new Error('Please set both opening and closing hours.'));
        }

        const outletSetting = {
            ...this.pendingOutletSetting,
            hub_code: this.pendingOutletSetting.hub_code || getFromLocalStorage('hub').value,
            opening_time: openingTime,
            closing_time: closingTime,
        };

        return api
            .updateOutletSetting({
                date: moment().format('DD-MM-YYYY'),
                outletSetting,
            })
            .then((result) => {
                this.outletSetting = outletSetting;
                this.pendingOutletSetting = {
                    ...outletSetting,
                    opening_time: this.formatOutletTime(outletSetting.opening_time),
                    closing_time: this.formatOutletTime(outletSetting.closing_time),
                };
                return result;
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    @action setSalePlatformOpen = (salePlatform, open) => {
        const salePlatformKey = this.getSalePlatformKey(salePlatform);

        this.salePlatforms = this.salePlatforms.map((entry) => {
            if (this.getSalePlatformKey(entry) !== salePlatformKey) {
                return entry;
            }

            const updatedSalePlatform = {
                ...entry,
                open,
                close_duration: open ? null : entry.close_duration,
                open_at: open ? null : entry.open_at,
                auto_open_at: open ? '' : entry.auto_open_at,
            };

            this.replaceUpdatedSalePlatform(updatedSalePlatform);
            return updatedSalePlatform;
        });
    };

    @action setSalePlatformCloseDuration = (salePlatform, closeDuration) => {
        const salePlatformKey = this.getSalePlatformKey(salePlatform);
        const normalizedDuration = closeDuration ? Number(closeDuration) : null;

        this.salePlatforms = this.salePlatforms.map((entry) => {
            if (this.getSalePlatformKey(entry) !== salePlatformKey) {
                return entry;
            }

            const openAt = normalizedDuration
                ? Date.now() + normalizedDuration * 60 * 1000
                : null;

            const updatedSalePlatform = {
                ...entry,
                open: normalizedDuration ? false : entry.open,
                close_duration: normalizedDuration,
                open_at: openAt,
                auto_open_at: openAt
                    ? (new Date(openAt).getDate() === new Date().getDate()
                        ? moment(openAt).local().format('hh:mm A')
                        : '11:59 PM')
                    : '',
            };

            this.replaceUpdatedSalePlatform(updatedSalePlatform);
            return updatedSalePlatform;
        });
    };

    @action clearSalePlatformUpdates = () => {
        this.updatedSalePlatforms = [];
    };

    @action handleUpdateHubStatusForSalePlatform = () => {
        this.outletControlLoading = true;

        const request = {
            grab_food_sale_platforms: this.updatedSalePlatforms.filter(
                (salePlatform) => salePlatform.aggregator === 'GrabFood'
            ),
            food_panda_sale_platforms: this.updatedSalePlatforms.filter(
                (salePlatform) => salePlatform.aggregator === 'FoodPanda'
            ),
            shopee_food_sale_platforms: this.updatedSalePlatforms.filter(
                (salePlatform) => salePlatform.aggregator === 'ShopeeFood'
            ),
        };

        return api.updateHubStatusForSalePlatforms(request)
            .then((result) => {
                if (result && result.code !== '200') {
                    throw new Error(result.message || 'Unable to update outlet status.');
                }

                this.updatedSalePlatforms = [];
                return this.handleGetHubStatus().then(() => result);
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    @action handleUpdateDispatchStatus = (dispatchForm) => {
        this.outletControlLoading = true;

        return api.updateDispatchStatus(dispatchForm)
            .then((result) => this.handleGetDispatchStatus().then(() => result))
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    @action handleAcknowledgeTruckAlert = (hub_code) => {
        api.acknowledgeTruckAlert(hub_code)
            .then((response) => {
                if (response) {
                    this.truckAlerts = [];
                } else {
                    UserStore.message =
                        'Truck alert not acknowledged successfully. Please try again';
                }
                this.recentTruckAlerts = [];
            })
            .catch((err) => {
                UserStore.message =
                    'Truck alert not acknowledged successfully. Please try again';
                this.recentTruckAlerts = [];
            });
    };

    @action handleAcknowledgeDriveThroughAlert = (hub_code) => {
        api.acknowledgeDriveThroughAlert(hub_code)
            .then((response) => {
                if (response) {
                    this.driveThroughAlerts = [];
                } else {
                    UserStore.message =
                        'Drive through alert not acknowledged successfully. Please try again';
                }
                this.driveThroughAlerts = [];
            })
            .catch((err) => {
                UserStore.message =
                    'Drive through alert not acknowledged successfully. Please try again';
                this.driveThroughAlerts = [];
            });
    };

    @action handleGetCashierScreenResponse = (
        forceLatest = false,
        date = this.date
    ) => {
        this.date = date;
        let ifModifiedSince = null;
        if (!forceLatest) {
            ifModifiedSince = this.routesLastModified;
        }

        api.getCashierScreenResponse(this.date, ifModifiedSince)
            .then((res) => {
                if (res.modified) {
                    const { lastModified, responseBody } = res;
                    const { pick_up_orders, in_store_orders } = responseBody;
                    this.routesLastModified = lastModified;

                    const sortedBags =
                        pick_up_orders &&
                        pick_up_orders.sort(
                            (a, b) => a.bag_number - b.bag_number
                        );
                    this.pickUpOrders = sortedBags ? [...sortedBags] : [];

                    // this.pickUpOrders = this.pickUpOrders.map((bag) => {
                    //     // const {
                    //     // //     amount_due,
                    //     // //     delivery_id,
                    //     // //     delivery_time,
                    //     // //     order_number,
                    //     // //     payment_method,
                    //     // //     pre_tax_net_total,
                    //     // // } = bag;
                    //     return {
                    //         bag,
                    //         start_date: delivery_time,
                    //     };
                    // });
                    const newInStoreOrders = in_store_orders
                        ? in_store_orders.filter(
                              (order) => order.items && order.items.length > 0
                          )
                        : [];

                    const sortedInStoreOrders =
                        newInStoreOrders &&
                        newInStoreOrders.sort(
                            (a, b) => a.order_date - b.order_date
                        );

                    this.inStoreOrders = sortedInStoreOrders
                        ? [...sortedInStoreOrders]
                        : [];
                }
                this.loading = false;
            })
            .catch((err) => {
                UserStore.message = err.message;
            });
    };

    @action handleConfirmPickupOrder = (deliveryId, orderNumber) => {
        return api.confirmPickupOrder(deliveryId)  // 👈 return the promise
            .then((response) => {
                const routeIndex = this.routes.findIndex(order => order.order_number == orderNumber);
                if (routeIndex >= 0) {
                    this.routes.splice(routeIndex, 1);
                } 

                const panduMasukOrderIndex = this.panduMasukOrders.findIndex(order => order.order_number == orderNumber);
                if (panduMasukOrderIndex >= 0) {
                    this.panduMasukOrders.splice(panduMasukOrderIndex, 1);
                } 

                const panduLaluOrderIndex = this.panduLaluOrders.findIndex(order => order.order_number == orderNumber);
                if (panduLaluOrderIndex >= 0) {
                    this.panduLaluOrders.splice(panduLaluOrderIndex, 1);
                } 

                const dineInOrderIndex = this.dineInOrders.findIndex(order => order.order_number == orderNumber);
                if (dineInOrderIndex >= 0) {
                    this.dineInOrders.splice(dineInOrderIndex, 1);
                }
            })
            .catch((err) => {
                console.error("Failed to confirm pickup:", err);
            });
    };

    @action handleConfirmDineInOrder = (deliveryId) => {
        api.confirmDineInOrder(deliveryId).catch((err) => console.log(err));
    };

    @action handleGetInstaServiceProduction = (forceLatest = false) => {
        let ifModifiedSince = null;
        if (!forceLatest) {
            ifModifiedSince = this.instaServiceLastModified;
        }

        api.getInstaServiceProduction(ifModifiedSince)
            .then((res) => {
                if (res.modified) {
                    const { lastModified, responseBody } = res;
                    this.instaServiceProductions = responseBody.list ? responseBody.list : [];
                    this.instaServiceLastModified = lastModified;
                    if (responseBody.current_stock) {
                        this.currentWarmerStocks = responseBody.current_stock;
                    }
                }
                this.loading = false;
            })
            .catch((err) => {
                UserStore.message = err.message;
            });
    };

    @action handleMarkOrderAsDone = (orderId, callback) => {
        let message;
        api.markOrderAsDone(orderId)
            .then((res) => {
                message = 'SUCCESS';
            })
            .catch((err) => {
                message = err.message;
            })
            .finally(() => {
                callback && callback(message);
            });
    }

    @action updateItemStatus = (lastScannedItem, inOutletOrders) => {
        let orders = [];
        if (inOutletOrders) {
            this.dineInOrders.filter((order) => (order.production_started_at && (order.sale_platform == "Pick Up" || order.sale_platform == "Dine In"))).map((dineInOrder) => (
                orders.push(dineInOrder)
            ));

            this.bags.filter((order) => (order.production_started_at && order.order_mode == "PICK_UP")).map((bag) => (
                orders.push(bag)
            ));
        }
        
        if (!inOutletOrders) {
            this.bags.filter((order) => order.production_started_at && order.order_mode != "PICK_UP").map((bag) => (
                orders.push(bag)
            ));

            this.dineInOrders.filter((order) => (order.production_started_at && (order.sale_platform != "Pick Up" && order.sale_platform != "Dine In"))).map((dineInOrder) => (
                orders.push(dineInOrder)
            ));
        }

        if (lastScannedItem) {
            const order = orders.find((order) => lastScannedItem.order_number === order.order_number);
            if (order) {
                let item = order.items.find((item) => 
                    item.today_menu_id === lastScannedItem.today_menu_id &&
                    item.incremental_number === lastScannedItem.incremental_number &&
                    item.status === "IN_PRODUCTION" && lastScannedItem.status === "ON_ROUTE"
                );
                if (item) {
                    item.status = "ON_ROUTE";
                }
            }
        }
    }

    @action displayErrorResultFunc = (message) => {
        this.scannedItemError = message;
        this.scannedItem = null;
        this.showScannedItemOnLiveDispatch = true;
        setTimeout(() => {
            this.showScannedItemOnLiveDispatch = false;
        }, 5 * 1000);
    }

    sortRoutes = (allRoutes) => {
        const sortedRoutes = allRoutes
            .filter(
                (route) =>
                    !route.bags || 
                    (route.bags.length &&
                    !route.bags.every(
                        (bag) => bag.status === this.CANCELLED
                    ))
            )
            .sort(
                (a, b) =>
                    a.start_date - b.start_date ||
                    a.dispatch_no - b.dispatch_no ||
                    a.route_no - b.route_no
            );
        return sortedRoutes ? [...sortedRoutes] : [];
    }

    initLiveDispatchData = (data) => {
        // Delivery and route case
        const { bags, routes, dine_in_orders } = data;
        const sortedBags =
            bags &&
            bags.sort((a, b) => a.bag_number - b.bag_number);
        this.bags = sortedBags ? [...sortedBags] : [];

        const pickUpOrders = this.bags
            .filter((bag) => bag.customer_self_pickup_at_outlet && bag.order_mode != "PANDU_MASUK" && bag.order_mode != "PANDU_LALU")
            .map((bag) => {
                const {
                    amount_due,
                    delivery_id,
                    delivery_time,
                    order_number,
                    payment_method,
                    pre_tax_net_total,
                } = bag;
                return {
                    isPickUpOrder: true,
                    amount_due,
                    order_number,
                    delivery_id,
                    start_date: delivery_time,
                    payment_method,
                    pre_tax_net_total,
                    bags: [bag],
                };
            });

        const panduMasukOrders = this.bags
            .filter((bag) => bag.customer_self_pickup_at_outlet && bag.order_mode == "PANDU_MASUK")
            .map((bag) => {
                const {
                    amount_due,
                    delivery_id,
                    delivery_time,
                    order_number,
                    payment_method,
                    pre_tax_net_total,
                } = bag;
                return {
                    isPickUpOrder: true,
                    amount_due,
                    order_number,
                    delivery_id,
                    start_date: delivery_time,
                    payment_method,
                    pre_tax_net_total,
                    bags: [bag],
                };
            });

        const panduLaluOrders = this.bags
            .filter((bag) => bag.customer_self_pickup_at_outlet && bag.order_mode == "PANDU_LALU")
            .map((bag) => {
                const {
                    amount_due,
                    delivery_id,
                    delivery_time,
                    order_number,
                    payment_method,
                    pre_tax_net_total,
                } = bag;
                return {
                    isPickUpOrder: true,
                    amount_due,
                    order_number,
                    delivery_id,
                    start_date: delivery_time,
                    payment_method,
                    pre_tax_net_total,
                    bags: [bag],
                };
            });
        
        this.panduMasukOrders = panduMasukOrders;
        this.panduLaluOrders = panduLaluOrders;

        const allOrders = routes.concat(pickUpOrders);
        this.routes = this.sortRoutes(allOrders);
        
        // Dine in case
        const newDineInOrders = dine_in_orders
                ? dine_in_orders.filter(
                        (order) =>
                            (order.status !== "Completed" || order.order_mode === "PANDU_MASUK" || order.order_mode === "PANDU_LALU" ) &&                            
                            order.items &&
                            order.items.length > 0
                    )
                : [];

        const sortedDineInOrders =
            newDineInOrders &&
            newDineInOrders.sort(
                (a, b) => a.order_date - b.order_date
            );

        this.dineInOrders = sortedDineInOrders
            ? [...sortedDineInOrders]
            : [];

        if (newDineInOrders && newDineInOrders.length > 0) {
            newDineInOrders.forEach((order) => {
                if (order.order_mode === "PANDU_MASUK") {
                    this.panduMasukOrders.push(order);
                } else if (order.order_mode === "PANDU_LALU") {
                    this.panduLaluOrders.push(order);
                }
            })
        }
        this.panduMasukOrders.sort((a, b) => a.start_date - b.start_date);
        this.panduLaluOrders.sort((a, b) => a.start_date - b.start_date);
        this.syncPanduMasukCustomerWaitingWithActiveOrders();
    }

    populateChanges = (data) => {
        const { bags, routes, dine_in_orders } = data;
        let hasNewOrder = false;
        if (bags && bags.length) {
            const bag = bags[0];
            if (!this.routes) {
                this.routes = [];
            }

            if (bag.customer_self_pickup_at_outlet && bag.order_mode != "PANDU_MASUK" && bag.order_mode != "PANDU_LALU") {
                // Replace or add scheduled pickup order
                const {
                    amount_due,
                    delivery_id,
                    delivery_time,
                    order_number,
                    payment_method,
                    pre_tax_net_total,
                } = bag;
                const pickupOrder = {
                    isPickUpOrder: true,
                    amount_due,
                    order_number,
                    delivery_id,
                    start_date: delivery_time,
                    payment_method,
                    pre_tax_net_total,
                    bags: [bag],
                };
                const index = this.routes.findIndex((order) => {return order.order_number == pickupOrder.order_number});
                if (index >= 0) {
                    this.routes.splice(index, 1);
                } 
                if (bag.status != 'Delivered' && bag.status != 'Cancelled') {
                    this.routes.push(pickupOrder);
                }
                this.routes = this.sortRoutes(this.routes);   

            } else if (bag.customer_self_pickup_at_outlet && (bag.order_mode == "PANDU_MASUK" || bag.order_mode == "PANDU_LALU")) {
                if (bag.order_mode == "PANDU_MASUK") {
                    const {
                        amount_due,
                        delivery_id,
                        delivery_time,
                        order_number,
                        payment_method,
                        pre_tax_net_total,
                    } = bag;
                    const panduMasukOrder = {
                        isPickUpOrder: true,
                        amount_due,
                        order_number,
                        delivery_id,
                        start_date: delivery_time,
                        payment_method,
                        pre_tax_net_total,
                        bags: [bag],
                    };
                    const index = this.panduMasukOrders.findIndex((order) => {return order.order_number == panduMasukOrder.order_number});
                    if (index >= 0) {
                        this.panduMasukOrders.splice(index, 1);
                    } 
                    if (bag.status != 'Delivered' && bag.status != 'Cancelled') {
                        this.panduMasukOrders.push(panduMasukOrder);
                    }
                }

                if (bag.order_mode == "PANDU_LALU") {
                    const {
                        amount_due,
                        delivery_id,
                        delivery_time,
                        order_number,
                        payment_method,
                        pre_tax_net_total,
                    } = bag;
                    const panduLaluOrder = {
                        isPickUpOrder: true,
                        amount_due,
                        order_number,
                        delivery_id,
                        start_date: delivery_time,
                        payment_method,
                        pre_tax_net_total,
                        bags: [bag],
                    };
                    const index = this.panduLaluOrders.findIndex((order) => {return order.order_number == panduLaluOrder.order_number});
                    if (index >= 0) {
                        this.panduLaluOrders.splice(index, 1);
                    } 
                    if (bag.status != 'Delivered' && bag.status != 'Cancelled') {
                        this.panduLaluOrders.push(panduLaluOrder);
                    }
                }

                this.panduMasukOrders.sort((a, b) => a.start_date - b.start_date);
                this.panduLaluOrders.sort((a, b) => a.start_date - b.start_date);
            
            }

            const bagIndex = this.bags.findIndex((order) => {return order.order_number == bag.order_number});
            if (bagIndex < 0 && bag.status != 'Delivered' && bag.status != 'Cancelled') {
                hasNewOrder = true;
            }
            if (bagIndex >= 0) {
                this.bags.splice(bagIndex, 1);
            }
            if (bag.status != 'Delivered' && bag.status != 'Cancelled' && !bag.rider_started_at) {
                this.bags.push(bag);
            }
        }

        if (routes && routes.length) {
            // Replace or add route
            const newRoute = routes[0];
            const index = this.routes.findIndex((route) => {return route.id == newRoute.id});
            if (index >= 0) {
                this.routes.splice(index, 1, newRoute);
            } else {
                hasNewOrder = true;
                this.routes.push(newRoute);
            }
            this.routes = this.sortRoutes(this.routes);

            // Add new order from route to bags
            newRoute.bags.forEach((bag) => {
                const bagIndex = this.bags.findIndex((order) => {return order.order_number == bag.order_number});
                if (bagIndex >= 0) {
                    this.bags.splice(index, 1);
                }
                if (bag.status != 'Delivered' && bag.status != 'Cancelled') {
                    this.bags.push(bag);
                }
            });
        }

        if (dine_in_orders && dine_in_orders.length) {
            const dineInOrder = dine_in_orders[0];
            const index = this.dineInOrders.findIndex((order) => {return order.order_number == dineInOrder.order_number});
            if (index < 0 && dineInOrder.status != 'Completed') {
                hasNewOrder = true;
            }
            if (index >= 0) {
                this.dineInOrders.splice(index, 1);
            }

            if (dineInOrder.order_mode === "PANDU_MASUK") {
                const panduMasukOrderIndex = this.panduMasukOrders.findIndex((order) => {return order.order_number == dineInOrder.order_number});
                this.panduMasukOrders.splice(panduMasukOrderIndex, 1);
            } else if (dineInOrder.order_mode === "PANDU_LALU") {
                const panduLaluOrderIndex = this.panduLaluOrders.findIndex((order) => {return order.order_number == dineInOrder.order_number});
                this.panduLaluOrders.splice(panduLaluOrderIndex, 1);
            }

            if (dineInOrder.status != 'Completed') {
                this.dineInOrders.push(dineInOrder);

                if (dineInOrder.order_mode === "PANDU_MASUK") {
                    this.panduMasukOrders.push(dineInOrder);
                } else if (dineInOrder.order_mode === "PANDU_LALU") {
                    this.panduLaluOrders.push(dineInOrder);
                }
            } else if (dineInOrder.order_mode === "PANDU_MASUK" || dineInOrder.order_mode === "PANDU_LALU") {
                this.dineInOrders.push(dineInOrder);

                if (dineInOrder.order_mode === "PANDU_MASUK") {
                    this.panduMasukOrders.push(dineInOrder);
                } else if (dineInOrder.order_mode === "PANDU_LALU") {
                    this.panduLaluOrders.push(dineInOrder);
                }
            } else if (dineInOrder.order_mode === "DINE_IN" && !dineInOrder.mark_done_time_stamp && 
                    dineInOrder.prepared_at && Date.now() - new Date(dineInOrder.prepared_at).getTime() < 1 * 60 * 1000) {
                    this.dineInOrders.push(dineInOrder);
            }
          
            const sortedDineInOrders = this.dineInOrders.sort(
                (a, b) => a.order_date - b.order_date
            );
            this.dineInOrders = sortedDineInOrders
                ? [...sortedDineInOrders]
                : [];

            this.panduMasukOrders.sort((a, b) => a.start_date - b.start_date);
            this.panduLaluOrders.sort((a, b) => a.start_date - b.start_date);
        }

        this.syncPanduMasukCustomerWaitingWithActiveOrders();

        if (hasNewOrder) {
            this.newOrderAudio.play().catch((err) => {
                console.log('Error playing new order sound');
            });
        }
    }

    populateIncomingOrderChanges = (data) => {
        const { in_cart_orders } = data;
        if (in_cart_orders) {
            this.inCartOrders = in_cart_orders;
        }
    }

    populateWarmerStockChanges = (data) => {
        const { warmer_response } = data;

        if (warmer_response) {
            this.instaServiceProductions = warmer_response.list ? warmer_response.list : [];
            if (warmer_response.current_stock) {
                this.currentWarmerStocks = warmer_response.current_stock;
            }
        }
    }

    populateDriveThroughAlerts = (data) => {
        const { drive_through_alert } = data;
        if (drive_through_alert) {
            this.driveThroughAlerts = [drive_through_alert];
        } else {
            this.driveThroughAlerts = [];
        }
    }

    populateCarArrivedAlerts = (data) => {
        const { car_arrived_alert } = data;
        if (car_arrived_alert) {
            this.carArrivedAlerts = [car_arrived_alert];
            this.rememberPanduMasukCustomerWaiting(car_arrived_alert.bag_number);
        } else {
            this.carArrivedAlerts = [];
        }
    }

    populateNeedHelpAlerts = (data) => {
        const { need_help_alert } = data;
        if (need_help_alert) {
            this.needHelpAlerts = [need_help_alert];
        } else {
            this.needHelpAlerts = [];
        }
    }

    @action updateOrderData = (dataStr) => {
        let data = JSON.parse(dataStr);
        if (data.type == 'init') {
            this.initLiveDispatchData(data);
        } else if (data.type == 'update') {
            this.populateChanges(data);
        }  else if (data.type == 'updateIncomingOrder') {
            this.populateIncomingOrderChanges(data);
        }  else if (data.type == 'updateWarmerStock') {
            this.populateWarmerStockChanges(data);
        } else if (data.type == 'driveThroughAlert') {
            this.populateDriveThroughAlerts(data);
        } else if (data.type == 'carArrivedAlert') {
            this.populateCarArrivedAlerts(data);
        } else if (data.type == 'needHelpAlert') {
            this.populateNeedHelpAlerts(data);
        }
    };

    @action triggerAggregatorMenuSync = () => {
        api.triggerAggregatorMenuSync()
            .then((response) => {
                UserStore.message = 'Aggregator menu sync triggered successfully';
            })
            .catch((err) => {
                UserStore.message = 'Can update menu only after 10 mins. Please try again later';
            });
    };

    @action handleGetVoiceReminders = () => {
        api.getVoiceReminders()
            .then((response) => response.json())
            .then((data) => {
                this.voiceReminders = data || [];
            })
            .catch((err) => console.log(err));
    };

    @action
    handlePauseOutletTemporarily = (durationMinutes) => {
        this.outletControlLoading = true;

        return api.pauseOutletTemporarily(durationMinutes)
            .then((result) => {
                if (result && result.code && result.code !== '200') {
                    throw new Error(result.message || 'Unable to pause outlet.');
                }

                return this.handleGetOutletPausedStatus().then(() => result);
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    @action
    handleUnpauseOutlet = () => {
        this.outletControlLoading = true;

        return api.unpauseOutlet()
            .then((result) => {
                if (result && result.code && result.code !== '200') {
                    throw new Error(result.message || 'Unable to open outlet.');
                }

                return this.handleGetOutletPausedStatus().then(() => result);
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            })
            .finally(() => {
                this.outletControlLoading = false;
            });
    };

    @action
    handleGetOutletPausedStatus = () =>
        api.getOutletPausedStatus()
            .then((isOutletPaused) => {
                this.outletPaused = Boolean(isOutletPaused);
                return this.outletPaused;
            })
            .catch((err) => {
                UserStore.message = err.message;
                throw err;
            });
}

const store = new LiveDispatchStore();
export default store;
